import { Button } from "components/commons/Button";
import { Link } from "components/commons/Link";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/router";
import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { localizePath } from "src/utils/localizedRoutes";
import Windows from "@/assets/img/icons/download.svg";

const FeatureSvg = ({ children }: { children: ReactNode }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

type FeatureItem = {
  nameKey: string;
  descriptionKey: string;
  href?: string;
  isNew: boolean;
  icon: ReactNode;
};

const FeatureIcon = ({ item }: { item: FeatureItem }) => (
  <span
    className={twMerge(
      "flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] border",
      item.isNew
        ? "bg-[#fff6dd] border-[#f3dc9a] text-[#8a5a00]"
        : "bg-white border-[#dfe5eb] text-[#0b6f9b]",
    )}
  >
    {item.icon}
  </span>
);

const NewBadge = () => (
  <span className="inline-flex h-5 items-center rounded-full bg-[#ffbe1d] px-2 text-[11px] font-black tracking-wide text-black">
    NOVO
  </span>
);

export default function Features() {
  const t = useTranslations("home/features");
  const router = useRouter();

  const panels: {
    eyebrowKey: string;
    headingKey: string;
    illustration: string;
    illustrationAlt: string;
    features: FeatureItem[];
  }[] = [
    {
      eyebrowKey: "panel-1-eyebrow",
      headingKey: "panel-1-heading",
      illustration: "/features/illustrations/estimating.svg",
      illustrationAlt: "Planta em perspetiva com uma parede medida e cotas",
      features: [
        {
          nameKey: "panel-1-feature-9-name",
          descriptionKey: "panel-1-feature-9-description",
          href: "/software/orcamentacao",
          isNew: true,
          icon: (
            <FeatureSvg>
              <path d="M12 3l1.8 4.7 4.7 1.8-4.7 1.8L12 16l-1.8-4.7-4.7-1.8 4.7-1.8z" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-1-feature-1-name",
          descriptionKey: "panel-1-feature-1-description",
          href: "/software/orcamentacao/importar-medicoes-de-modelos-bim",
          isNew: true,
          icon: (
            <FeatureSvg>
              <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />
              <path d="M4 7.5l8 4.5 8-4.5M12 12v9" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-1-feature-2-name",
          descriptionKey: "panel-1-feature-2-description",
          isNew: true,
          icon: (
            <FeatureSvg>
              <path d="M6 3h8l4 4v14H6z" />
              <path d="M14 3v4h4M12 10v7M9 14l3 3 3-3" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-1-feature-3-name",
          descriptionKey: "panel-1-feature-3-description",
          isNew: true,
          icon: (
            <FeatureSvg>
              <path d="M4 8h13M13 4l4 4-4 4M20 16H7M11 12l-4 4 4 4" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-1-feature-4-name",
          descriptionKey: "panel-1-feature-4-description",
          href: "/software/orcamentacao/efetuar-ou-verificar-as-medicoes",
          isNew: false,
          icon: (
            <FeatureSvg>
              <path d="M3 17 17 3l4 4L7 21z" />
              <path d="M7 13l2 2M10 10l2 2M13 7l2 2" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-1-feature-5-name",
          descriptionKey: "panel-1-feature-5-description",
          href: "/software/orcamentacao/compor-os-precos-do-orcamento",
          isNew: false,
          icon: (
            <FeatureSvg>
              <ellipse cx={12} cy={5} rx={8} ry={3} />
              <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-1-feature-6-name",
          descriptionKey: "panel-1-feature-6-description",
          href: "/software/orcamentacao/analisar-e-otimizar-o-orcamento",
          isNew: false,
          icon: (
            <FeatureSvg>
              <path d="M4 4v16h16" />
              <path d="M7 17c3-1 5-6 7-9s3-3 5-3" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-1-feature-7-name",
          descriptionKey: "panel-1-feature-7-description",
          href: "/software/orcamentacao/introduzir-margens-no-orcamento",
          isNew: false,
          icon: (
            <FeatureSvg>
              <circle cx={12} cy={12} r={9} />
              <path d="M15.5 8.5a4.5 4.5 0 1 0 0 7M7 10.5h6M7 13.5h6" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-1-feature-8-name",
          descriptionKey: "panel-1-feature-8-description",
          href: "/software/orcamentacao/propriedades-e-impressao",
          isNew: false,
          icon: (
            <FeatureSvg>
              <path d="M6 3h8l4 4v14H6z" />
              <path d="M14 3v4h4M9 12h6M9 16h6" />
            </FeatureSvg>
          ),
        },
      ],
    },
    {
      eyebrowKey: "panel-2-eyebrow",
      headingKey: "panel-2-heading",
      illustration: "/features/illustrations/planning.svg",
      illustrationAlt:
        "Plataforma em perspetiva com barras de tarefas em cascata",
      features: [
        {
          nameKey: "panel-2-feature-1-name",
          descriptionKey: "panel-2-feature-1-description",
          href: "/software/planeamento/elaborar-o-plano-de-trabalhos",
          isNew: true,
          icon: (
            <FeatureSvg>
              <path d="M12 3l1.8 4.7 4.7 1.8-4.7 1.8L12 16l-1.8-4.7-4.7-1.8 4.7-1.8z" />
              <path d="M19 15l.8 2.2 2.2.8-2.2.8L19 21l-.8-2.2-2.2-.8 2.2-.8z" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-2-feature-2-name",
          descriptionKey: "panel-2-feature-2-description",
          href: "/software/planeamento/planeamento-externo",
          isNew: true,
          icon: (
            <FeatureSvg>
              <rect x={3} y={5} width={18} height={16} rx={2} />
              <path d="M3 10h18M8 3v4M16 3v4M8 15.5h7M13 13.5l2 2-2 2" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-2-feature-3-name",
          descriptionKey: "panel-2-feature-3-description",
          href: "/software/planeamento/elaborar-o-plano-de-trabalhos",
          isNew: false,
          icon: (
            <FeatureSvg>
              <rect x={3} y={4} width={9} height={3.5} rx={1} />
              <rect x={7} y={10.25} width={10} height={3.5} rx={1} />
              <rect x={12} y={16.5} width={9} height={3.5} rx={1} />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-2-feature-4-name",
          descriptionKey: "panel-2-feature-4-description",
          href: "/software/planeamento/obter-o-plano-de-pagamentos",
          isNew: false,
          icon: (
            <FeatureSvg>
              <rect x={2.5} y={5.5} width={19} height={13} rx={2} />
              <path d="M2.5 10h19M6 14.5h4" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-2-feature-5-name",
          descriptionKey: "panel-2-feature-5-description",
          href: "/software/planeamento/plano-de-mao-de-obra-e-equipamentos",
          isNew: false,
          icon: (
            <FeatureSvg>
              <path d="M5 15a7 7 0 0 1 14 0" />
              <path d="M10 8.3V12M14 8.3V12" />
              <path d="M3 15h18v3H3z" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-2-feature-6-name",
          descriptionKey: "panel-2-feature-6-description",
          href: "/software/planeamento/criar-o-plano-de-compras",
          isNew: false,
          icon: (
            <FeatureSvg>
              <circle cx={9} cy={20} r={1.5} />
              <circle cx={17} cy={20} r={1.5} />
              <path d="M3 4h2l2.4 11h11L21 8H6.2" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-2-feature-7-name",
          descriptionKey: "panel-2-feature-7-description",
          href: "/software/planeamento/criar-a-memoria-descritiva",
          isNew: false,
          icon: (
            <FeatureSvg>
              <path d="M6 3h8l4 4v14H6z" />
              <path d="M14 3v4h4M9 11h6M9 14.5h6M9 18h4" />
            </FeatureSvg>
          ),
        },
      ],
    },
    {
      eyebrowKey: "panel-3-eyebrow",
      headingKey: "panel-3-heading",
      illustration: "/features/illustrations/construction-management.svg",
      illustrationAlt:
        "Estaleiro em perspetiva com materiais e um gráfico de custos previstos e reais",
      features: [
        {
          nameKey: "panel-3-feature-1-name",
          descriptionKey: "panel-3-feature-1-description",
          href: "/software/gestao-de-obra/revisao-de-precos",
          isNew: true,
          icon: (
            <FeatureSvg>
              <circle cx={7} cy={7} r={2.5} />
              <circle cx={17} cy={17} r={2.5} />
              <path d="M19 5 5 19" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-3-feature-2-name",
          descriptionKey: "panel-3-feature-2-description",
          href: "/software/gestao-de-obra/elaborar-o-bloco-de-notas",
          isNew: true,
          icon: (
            <FeatureSvg>
              <rect x={5} y={3} width={14} height={18} rx={2} />
              <path d="M9 8h6M9 12h6M9 16h3" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-3-feature-3-name",
          descriptionKey: "panel-3-feature-3-description",
          href: "/software/gestao-de-obra/gerir-a-lista-de-fornecedores",
          isNew: true,
          icon: (
            <FeatureSvg>
              <path d="M3 21h18M5 21V8l7-4 7 4v13" />
              <path d="M9 21v-6h6v6" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-3-feature-4-name",
          descriptionKey: "panel-3-feature-4-description",
          href: "/software/gestao-de-obra/elaborar-os-autos-de-medicao",
          isNew: false,
          icon: (
            <FeatureSvg>
              <rect x={5} y={3} width={14} height={18} rx={2} />
              <path d="M8 7h8M8.5 12h1M12 12h1M15 12h.5M8.5 16h1M12 16h1M15 16h.5" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-3-feature-5-name",
          descriptionKey: "panel-3-feature-5-description",
          href: "/software/gestao-de-obra/controlar-as-compras",
          isNew: false,
          icon: (
            <FeatureSvg>
              <path d="M3 6h11v10H3zM14 10h4l3 3v3h-7" />
              <circle cx={7} cy={18} r={1.8} />
              <circle cx={17} cy={18} r={1.8} />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-3-feature-6-name",
          descriptionKey: "panel-3-feature-6-description",
          href: "/software/gestao-de-obra/elaborar-e-gerir-os-trabalhos-a-mais",
          isNew: false,
          icon: (
            <FeatureSvg>
              <path d="M6 3h8l4 4v14H6z" />
              <path d="M14 3v4h4M12 11v6M9 14h6" />
            </FeatureSvg>
          ),
        },
        {
          nameKey: "panel-3-feature-7-name",
          descriptionKey: "panel-3-feature-7-description",
          href: "/software/gestao-de-obra/controlar-os-custos-da-obra",
          isNew: false,
          icon: (
            <FeatureSvg>
              <path d="M3 17l6-6 4 4 8-8" />
              <path d="M15 7h6v6" />
            </FeatureSvg>
          ),
        },
      ],
    },
  ];

  return (
    <div id="features" className="bg-white py-16 px-4 md:py-20">
      <div className="mx-auto flex max-w-6xl flex-col gap-14">
        <header className="flex flex-col items-center gap-[18px] text-center">
          <p className="m-0 text-[13px] font-extrabold tracking-[0.16em] text-[#0b6f9b]">
            {t("eyebrow").toUpperCase()}
          </p>
          <h2 className="m-0 max-w-3xl text-[32px] font-extrabold leading-[1.12] text-[#1f2d3d] md:text-[48px]">
            {t("title")}
          </h2>
          <p className="m-0 max-w-xl text-lg leading-[1.6] text-[#4a525c]">
            {t("subtitle")}
          </p>

          <div className="mt-2.5 flex flex-wrap items-center justify-center gap-2.5">
            {panels.map((panel, index) => (
              <span key={panel.headingKey} className="contents">
                {index > 0 && (
                  <svg
                    className="hidden sm:block"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8a96a3"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                )}
                <span className="inline-flex items-center gap-2.5 rounded-full bg-[#eef8fd] py-2 pl-2 pr-4 text-[15px] font-extrabold text-[#1f2d3d]">
                  <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#4ac4f3] text-[13px] text-[#0d2230]">
                    {index + 1}
                  </span>
                  {t(`tab-${index + 1}-label`)}
                </span>
              </span>
            ))}
          </div>

          <p className="m-0 flex flex-wrap items-center justify-center gap-2 text-sm font-bold text-[#4a525c]">
            <span className="inline-flex h-5 items-center rounded-full bg-[#ffbe1d] px-2 text-[11px] font-black tracking-wide text-black">
              {t("news-badge")}
            </span>
            {t("news-text")}
            <Link
              href={localizePath("/software/novidades", router.locale)}
              text={t("news-link")}
              className="font-extrabold text-[#0b6f9b] hover:underline"
            />
          </p>
        </header>

        <div className="grid gap-6 md:grid-cols-3">
          {panels.map((panel) => (
            <article
              key={panel.headingKey}
              className="flex flex-col overflow-hidden rounded-[20px] border border-[#e3e8ed] bg-[#f6f8fa]"
            >
              <Image
                src={panel.illustration}
                alt={panel.illustrationAlt}
                width={360}
                height={200}
                style={{ aspectRatio: "360 / 200" }}
                className="block h-auto w-full"
              />
              <div className="flex flex-grow flex-col gap-[22px] px-7 pb-[30px] pt-7">
                <div className="flex flex-col gap-2">
                  <p className="m-0 text-[13px] font-extrabold tracking-[0.12em] text-[#0b6f9b]">
                    {t(panel.eyebrowKey)}
                  </p>
                  <h3 className="m-0 text-[26px] font-extrabold leading-[1.2] text-[#1f2d3d]">
                    {t(panel.headingKey)}
                  </h3>
                </div>
                <ul className="m-0 flex list-none flex-col gap-4 p-0">
                  {panel.features.map((feature) => (
                    <li
                      key={feature.nameKey}
                      className="flex items-start gap-3.5"
                    >
                      <FeatureIcon item={feature} />
                      <span className="flex flex-col gap-0.5">
                        <span className="flex flex-wrap items-center gap-2">
                          {feature.href ? (
                            <Link
                              href={localizePath(feature.href, router.locale)}
                              text={t(feature.nameKey)}
                              className="text-base font-extrabold text-[#1f2d3d] hover:text-[#0b6f9b]"
                            />
                          ) : (
                            <span className="text-base font-extrabold text-[#1f2d3d]">
                              {t(feature.nameKey)}
                            </span>
                          )}
                          {feature.isNew && <NewBadge />}
                        </span>
                        <span className="text-[15px] leading-[1.45] text-[#4a525c]">
                          {t(feature.descriptionKey)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-6 rounded-[20px] bg-[#16181d] px-6 py-9 sm:px-10">
          <div className="flex min-w-0 flex-1 basis-[420px] flex-col gap-1.5 text-left">
            <p className="m-0 text-2xl font-extrabold leading-[1.3] text-white md:text-left">
              {t("cta-title")}
            </p>
            <p className="m-0 text-base leading-[1.5] text-[#c9ced6]">
              {t("cta-subtitle")}
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
            <Link href="/download" prefetch={false} className="w-full sm:w-auto">
              <motion.div
                className="w-full sm:w-auto"
                animate={{ scale: [1, 1.04, 1] }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.96 }}
              >
                <Button
                  variant="primary"
                  className="inline-flex w-full items-center justify-center gap-2.5 whitespace-nowrap bg-[#ffbe1d] border-[#ffbe1d] text-black sm:w-auto"
                >
                  <Image alt="" width={18} height={18} src={Windows} />
                  {t("cta-primary-button")}
                </Button>
              </motion.div>
            </Link>
            <Link href="/software" prefetch={false} className="w-full sm:w-auto">
              <Button
                variant="secondary"
                className="w-full whitespace-nowrap bg-transparent border-white border-[1.5px] sm:w-auto"
              >
                {t("cta-secondary-button")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
